'use client'
import { useEffect, useRef, useState } from 'react'

/** Keep the hovered detail aligned, then pan to the full photo at its edges. */
function loupeOffset(pointer: number, frame: number, image: number, crop: number, start: number, lens: number, zoom: number) {
  const aligned = (frame - image) * crop * zoom - pointer * zoom + pointer - start
  const nearStart = Math.max(0, 1 - pointer / (lens / 2))
  const nearEnd = Math.max(0, 1 - (frame - pointer) / (lens / 2))
  const end = lens - image * zoom
  return Math.max(end, Math.min(0, aligned * (1 - nearStart - nearEnd) + end * nearEnd))
}

export default function PhotoMagnifier({ src, alt }: { src: string; alt: string }) {
  const photo = useRef<HTMLImageElement>(null)
  const [point, setPoint] = useState<{ x: number; y: number } | null>(null)
  const [frame, setFrame] = useState({ width: 0, height: 0, naturalWidth: 0, naturalHeight: 0 })
  useEffect(() => {
    const hide = () => setPoint(null)
    const observer = new ResizeObserver(hide)
    if (photo.current) observer.observe(photo.current)
    window.addEventListener('resize', hide)
    window.addEventListener('scroll', hide, true)
    return () => { observer.disconnect(); window.removeEventListener('resize', hide); window.removeEventListener('scroll', hide, true) }
  }, [])
  const measure = () => {
    const image = photo.current
    if (!image?.naturalWidth) return null
    const rect = image.getBoundingClientRect()
    setFrame({ width: rect.width, height: rect.height, naturalWidth: image.naturalWidth, naturalHeight: image.naturalHeight })
    return rect
  }
  const showAt = (clientX: number, clientY: number) => {
    const rect = measure()
    if (rect) setPoint({ x: Math.max(0, Math.min(rect.width, clientX - rect.left)), y: Math.max(0, Math.min(rect.height, clientY - rect.top)) })
  }
  const scale = Math.max(frame.width / frame.naturalWidth, frame.height / frame.naturalHeight)
  const zoom = Math.min(1.5, 1 / scale)
  const width = frame.naturalWidth * scale, height = frame.naturalHeight * scale
  const lens = Math.min(180, frame.width * .48)
  const left = point ? Math.max(0, Math.min(frame.width - lens, point.x - lens / 2)) : 0
  const top = point ? Math.max(0, Math.min(frame.height - lens, point.y - lens / 2)) : 0
  return <button type="button" className="shirt-photo-zoom" aria-label={`Zoom photo: ${alt}`} aria-pressed={!!point}
    onPointerMove={event => { if (event.pointerType === 'mouse') showAt(event.clientX, event.clientY) }}
    onPointerLeave={() => setPoint(null)} onPointerCancel={() => setPoint(null)} onBlur={() => setPoint(null)}
    onClick={event => { if (event.detail === 0) { const rect = measure(); if (rect) setPoint(point ? null : { x: rect.width / 2, y: rect.height / 2 }) } else if (!matchMedia('(hover: hover) and (pointer: fine)').matches) { if (point) setPoint(null); else showAt(event.clientX, event.clientY) } }}
    onKeyDown={event => {
      if (event.key === 'Escape' && point) { event.stopPropagation(); setPoint(null) }
      if (!event.key.startsWith('Arrow')) return
      event.preventDefault()
      const rect = measure()
      if (!rect) return
      setPoint(p => ({ x: Math.max(0, Math.min(rect.width, (p?.x ?? rect.width / 2) + (event.key === 'ArrowRight' ? 24 : event.key === 'ArrowLeft' ? -24 : 0))), y: Math.max(0, Math.min(rect.height, (p?.y ?? rect.height / 2) + (event.key === 'ArrowDown' ? 24 : event.key === 'ArrowUp' ? -24 : 0))) }))
    }}>
    <img ref={photo} className="shirt-photo" src={src} alt={alt} width={1400} height={1400} fetchPriority="high" draggable={false} onLoad={() => setPoint(null)} />
    {point && frame.width > 0 && <span className="shirt-photo-lens" aria-hidden="true" style={{
      width: lens, height: lens, left, top,
      backgroundImage: `url("${src}")`, backgroundSize: `${width * zoom}px ${height * zoom}px`,
      backgroundPosition: `${loupeOffset(point.x, frame.width, width, .5, left, lens, zoom)}px ${loupeOffset(point.y, frame.height, height, .57, top, lens, zoom)}px`,
    }} />}
  </button>
}
